import { useEffect, useState, useCallback, useMemo } from "react";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import UploadIcon from "@mui/icons-material/Upload";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useTranslation } from "react-i18next";
import httpClient from "api/httpClient";
import ConfirmationDialog from "components/Common/ConfirmationDialog";
import { CustomTablePagination } from "components/Common/CustomTablePagination";
import { CustomMenuButton } from "components/Common/CustomMenuButton";
import ImportExcelDialog from "components/Common/ImportExcelDialog";
import SchemaDialogForm from "components/Form/SchemaDialogForm";
import PoliciesTableData from "./data/PoliciesTableData";
import { policiesFormSchema, policiesRecord } from "./data/PoliciesSchemas";
import { handleApiError } from "utils/apiError";

function Policies() {
  const apiBase = process.env.REACT_APP_API_URL;
  const { t } = useTranslation();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(policiesRecord);
  const [saving, setSaving] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [cards, setCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [policyTypes, setPolicyTypes] = useState([]);
  const [policyTypesLoading, setPolicyTypesLoading] = useState(false);

  const [policyItems, setPolicyItems] = useState([]);
  const [policyItemsLoading, setPolicyItemsLoading] = useState(false);
  const [insurees, setInsurees] = useState([]);

  // Load Benefit Items
  const loadPolicyItems = useCallback(
    async (search = "") => {
      try {
        setPolicyItemsLoading(true);
        const url = `${apiBase}/api/policy-items/search-list?page=0&size=50&q=${search}`;
        const res = await httpClient.get(url);
        const data = res.data.content || [];
        setPolicyItems(data.map((item) => ({ ...item, label: item.name })));
      } catch (err) {
        console.error("Failed to load policy items", err);
      } finally {
        setPolicyItemsLoading(false);
      }
    },
    [apiBase]
  );

  // Load Insurees (Beneficiaries)
  const loadInsurees = useCallback(
    async (search = "") => {
      try {
        const url = `${apiBase}/api/insurees/search-list?page=0&size=20&q=${search}`;
        const res = await httpClient.get(url);
        const data = res.data.content || [];
        setInsurees(
          data.map((i) => ({
            ...i,
            label: `${i.name} (${i.employeeId || "N/A"})`,
          }))
        );
      } catch (err) {
        console.error("Failed to load insurees", err);
      }
    },
    [apiBase]
  );

  const loadCards = useCallback(
    async (search = "") => {
      try {
        setCardsLoading(true);
        const url = `${apiBase}/api/cards/search-list?page=0&size=20&q=${search}`;
        const res = await httpClient.get(url);
        const cardData = res.data.content || [];
        setCards(
          cardData.map((card) => ({
            ...card,
            label: `${card.code} (${card.description})`,
          }))
        );
      } catch (err) {
        console.error("Failed to load cards", err);
      } finally {
        setCardsLoading(false);
      }
    },
    [apiBase]
  );

  const loadPolicyTypes = useCallback(
    async (search = "") => {
      try {
        setPolicyTypesLoading(true);
        const url = `${apiBase}/api/policy-types/search-list?page=0&size=20&q=${search}`;
        const res = await httpClient.get(url);
        const policyTypeData = res.data.content || [];
        setPolicyTypes(
          policyTypeData.map((policyType) => ({
            ...policyType,
            label: `${policyType.name} (${policyType.code})`,
          }))
        );
      } catch (err) {
        console.error("Failed to load policy types", err);
      } finally {
        setPolicyTypesLoading(false);
      }
    },
    [apiBase]
  );

  // Initial Loads
  useEffect(() => {
    loadPolicyItems();
    loadInsurees();
    loadCards();
    loadPolicyTypes();
  }, [loadPolicyItems, loadInsurees, loadCards, loadPolicyTypes]);

  // Memoized dependencies for the Dialog Form
  const formDeps = useMemo(
    () => ({
      cards,
      cardsLoading,
      onSearchCards: loadCards,
      policyTypes,
      policyTypesLoading,
      onSearchPolicyTypes: loadPolicyTypes,
      availablePolicyItems: policyItems,
      policyItemsLoading,
      onSearchPolicyItems: loadPolicyItems,
      insurees,
      onSearchInsurees: loadInsurees,
    }),
    [
      cards,
      cardsLoading,
      policyTypes,
      policyTypesLoading,
      policyItems,
      policyItemsLoading,
      insurees,
      loadCards,
      loadPolicyTypes,
      loadPolicyItems,
      loadInsurees,
    ]
  );

  const [sb, setSb] = useState({
    open: false,
    title: "",
    type: "error",
    message: "",
  });

  const showSnackBar = (title, type, message) => {
    setSb({ open: true, title, type, message });
  };

  const loadPolicies = async (q = "") => {
    try {
      setLoading(true);
      const url = q
        ? `${apiBase}/api/policies/search-list?page=${page}&size=${rowsPerPage}&q=${q}`
        : `${apiBase}/api/policies?page=${page}&size=${rowsPerPage}`;
      const res = await httpClient.get(url);
      setItems(res.data.content || []);
      setTotalElements(res.data.totalElements || 0);
    } catch (err) {
      handleApiError(err, { showSnackBar });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPolicies(searchInput);
  }, [page, rowsPerPage]);

  const onEditingRecordChange = async (rec) => {
    try {
      setLoading(true);
      const res = await httpClient.get(`${apiBase}/api/policies/${rec.id}`);
      const fullData = res.data;

      // 1. Logic for injecting into lists (Cards/PolicyTypes)
      // Since the API only gives IDs, we search our existing state.
      // If not found, you might need a small fetch, but for now,
      // let's ensure the data mapping is perfect.

      const preparedData = {
        ...fullData,
        // Ensure these match your Schema IDs exactly
        policyTypeId: fullData.policyTypeId || null,
        cardId: fullData.cardId || null,
        // CRITICAL: If insureeIds is null, Autocomplete will fail.
        // It MUST be an empty array.
        insureeIds: Array.isArray(fullData.insureeIds) ? fullData.insureeIds : [],
        // Ensure benefits are preserved
        benefits: fullData.benefits || [],
      };

      console.log("Final Prepared Data:", preparedData); // Check this in console!

      setEditingRecord(preparedData);
      setDialogOpen(true);
    } catch (err) {
      handleApiError(err, { showSnackBar });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      const payload = {
        ...data,
        policyTypeId: data.policyTypeId || null,
        cardId: data.cardId || null,
      };

      const isEdit = payload.id && Number(payload.id) > 0;
      const url = isEdit ? `${apiBase}/api/policies/${payload.id}` : `${apiBase}/api/policies`;

      await httpClient[isEdit ? "put" : "post"](url, payload);

      showSnackBar("Success", "success", `Policy ${isEdit ? "updated" : "created"}`);
      setDialogOpen(false);
      loadPolicies(searchInput);
    } catch (err) {
      handleApiError(err, { showSnackBar });
    } finally {
      setSaving(false);
    }
  };

  const onDeleteConfirmed = async () => {
    try {
      if (Array.isArray(itemToDelete)) {
        await httpClient.post(`${apiBase}/api/policies/bulk-delete`, { ids: itemToDelete });
      } else {
        await httpClient.delete(`${apiBase}/api/policies/${itemToDelete.id}`);
      }
      showSnackBar("Success", "success", "Deleted successfully");
      loadPolicies(searchInput);
    } catch (err) {
      handleApiError(err, { showSnackBar });
    } finally {
      setConfirmationDialogOpen(false);
    }
  };

  const onCreateNewRecord = () => {
    const defaultBenefits = policyItems.map((item) => ({
      id: null,
      policyItemId: item.id,
      limit: 0,
      countLimit: 0,
    }));

    setEditingRecord({
      ...policiesRecord,
      benefits: defaultBenefits,
      insurees: [],
    });

    setDialogOpen(true);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Card>
          <MDBox display="flex" justifyContent="space-between" p={3}>
            <MDBox display="flex" gap={1}>
              <MDButton variant="gradient" color="info" size="small" onClick={onCreateNewRecord}>
                Create Policy <Icon>add</Icon>
              </MDButton>
              <MDButton
                variant="gradient"
                color="dark"
                size="small"
                onClick={() => setImportOpen(true)}
              >
                Import <UploadIcon fontSize="small" />
              </MDButton>
            </MDBox>
            <MDBox display="flex" gap={1}>
              <MDInput
                size="small"
                label="Search Policies"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyUp={(e) => e.key === "Enter" && loadPolicies(searchInput)}
              />
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                onClick={() => loadPolicies(searchInput)}
              >
                Go
              </MDButton>
              <CustomMenuButton
                buttonTitle="Actions"
                items={[
                  {
                    key: "del",
                    text: "Delete Selected",
                    icon: "delete",
                    iconColor: "error",
                  },
                ]}
                onItemClicked={() => {
                  setItemToDelete(selectedIds);
                  setConfirmationDialogOpen(true);
                }}
              />
            </MDBox>
          </MDBox>

          <MDBox px={3} pb={3}>
            {loading ? (
              <MDTypography variant="button">Loading...</MDTypography>
            ) : (
              <PoliciesTableData
                data={items}
                selectedIds={selectedIds}
                onToggle={(id) =>
                  setSelectedIds((prev) =>
                    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                  )
                }
                onEdit={onEditingRecordChange}
                onDelete={(rec) => {
                  setItemToDelete(rec);
                  setConfirmationDialogOpen(true);
                }}
              />
            )}
            <CustomTablePagination
              page={page}
              rowsPerPage={rowsPerPage}
              totalItems={totalElements}
              onPageChange={setPage}
              onRowsPerPageChange={setRowsPerPage}
            />
          </MDBox>
        </Card>
      </MDBox>

      <SchemaDialogForm
        open={dialogOpen}
        title="Policy Details"
        schema={policiesFormSchema}
        initialRecord={editingRecord}
        saving={saving}
        onClose={() => setDialogOpen(false)}
        onSubmit={onSubmit}
        deps={formDeps}
      />

      <ConfirmationDialog
        open={confirmationDialogOpen}
        title="Confirm Delete"
        message="Are you sure you want to delete this policy?"
        onCancel={() => setConfirmationDialogOpen(false)}
        onOk={onDeleteConfirmed}
      />

      <ImportExcelDialog
        open={importOpen}
        title="Import Policies"
        endpoint={`${apiBase}/api/policies/import`}
        templateEndpoint={`${apiBase}/api/policies/template`}
        onClose={() => setImportOpen(false)}
        onDone={() => loadPolicies()}
        showSnackBar={showSnackBar}
      />

      <MDSnackbar
        color={sb.type}
        icon="notifications"
        title={sb.title}
        content={sb.message}
        open={sb.open}
        onClose={() => setSb({ ...sb, open: false })}
      />
    </DashboardLayout>
  );
}

export default Policies;
